import React, { CSSProperties, useEffect, useState } from 'react';

import { Box, Button, styled } from '@mui/material';

import { CloseIcon2 } from '../../../../assets/icons';
import { DataGridList } from '../../data-grid-list/data-grid-list';
import { ColorChooser } from './color-chooser';

type ProductTileBase = {
  title: string;
  collectionName: string;
  variants: {
    name: string;
    slug: string;
    hash: string;
    colorName: string;
    colors: number[][];
    image: React.ReactElement;
  }[];
  keyFacts?: string[];
  detailsPageLabel: string;
  variantsCountText: string;
  langPrefix: string;
  buttonComponent: React.ElementType;
  isNonClothing?: boolean;
};

export type ProductTileProps = ProductTileBase & {
  WrapperComponent?: React.ElementType<
    React.PropsWithChildren & {
      isExpanded: boolean;
      hideOnMobile?: boolean;
      isNonClothing?: boolean;
    }
  >;
  onSelect: (index: number) => void;
  onDeselect: (index: number) => void;
  hideOnMobile: boolean;
  index: number;
  slug: string;
};

export type ProductTileContentProps = ProductTileBase & {
  expanded: boolean;
  toggleExpansion: () => void;
};

export const ProductTileContent = ({
  title,
  collectionName,
  variants,
  detailsPageLabel,
  variantsCountText,
  keyFacts,
  langPrefix,
  buttonComponent,
  expanded = false,
  toggleExpansion,
  isNonClothing = false,
}: ProductTileContentProps) => {
  const [selectedVariant, setSelectedColor] = useState(0);
  const [hoveredVariant, setHoveredColor] = useState(selectedVariant);

  useEffect(() => {
    setHoveredColor(selectedVariant);
  }, [selectedVariant]);

  return (
    <ShadowContainer expanded={expanded}>
      <ImageContainer>
        {variants.map((variant, index) => (
          <ImageVariant key={variant.slug} active={index === hoveredVariant}>
            {variant.image}
          </ImageVariant>
        ))}
        <IconContainer expanded={expanded}>
          <Icon onClick={() => toggleExpansion()}>
            <CloseIcon2 />
          </Icon>
        </IconContainer>
      </ImageContainer>
      <ContentContainer expanded={expanded}>
        <Title>{title}</Title>
        <CollectionName>{collectionName}</CollectionName>
        {isNonClothing && keyFacts ? (
          <KeyFacts items={keyFacts} />
        ) : (
          <VariantsInfo>{variantsCountText}</VariantsInfo>
        )}
      </ContentContainer>
      {isNonClothing ? null : (
        <DetailsContainer expanded={expanded}>
          <ColorContainer>
            <ColorChooser
              colorVariants={variants.map((v) => v.colors)}
              onChange={(idx) => setSelectedColor(idx)}
              onMouseEnter={(idx) => setHoveredColor(idx)}
              onMouseLeave={() => setHoveredColor(selectedVariant)}
              activeColor={selectedVariant}
            ></ColorChooser>
          </ColorContainer>
          <ColorName>{variants[hoveredVariant].colorName}</ColorName>

          <Button
            component={buttonComponent}
            variant="text"
            color="primary"
            to={`${langPrefix}/${variants[selectedVariant].slug}`}
          >
            {detailsPageLabel}
          </Button>
        </DetailsContainer>
      )}
    </ShadowContainer>
  );
};

export const ProductTile = ({
  title,
  collectionName,
  variants,
  keyFacts,
  WrapperComponent = React.Fragment,
  detailsPageLabel,
  variantsCountText,
  onSelect,
  onDeselect,
  hideOnMobile,
  index,
  langPrefix,
  slug,
  buttonComponent,
  isNonClothing,
}: ProductTileProps) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const toggleExpansion = () => {
    if (isExpanded) {
      setIsExpanded(false);
      onDeselect(index);
    } else {
      setIsExpanded(true);
      onSelect(index);
    }
  };

  const tile = (
    expanded: boolean,
    position: 'absolute' | 'relative' = 'relative',
    hideOnMobile = false,
  ) => {
    const tileContent = (
      <ProductTileContent
        expanded={expanded}
        toggleExpansion={toggleExpansion}
        {...{
          title,
          collectionName,
          variants,
          keyFacts,
          detailsPageLabel,
          variantsCountText,
          langPrefix,
          buttonComponent,
          isNonClothing,
        }}
      />
    );

    return isNonClothing ? (
      <LinkContainer
        component={buttonComponent}
        to={`${langPrefix}/${slug}`}
        position={position}
      >
        {tileContent}
      </LinkContainer>
    ) : (
      <ExpansionContainer
        onClick={!expanded ? toggleExpansion : undefined}
        expanded={expanded}
        position={position}
        hideOnMobile={hideOnMobile}
      >
        {tileContent}
      </ExpansionContainer>
    );
  };

  return (
    <WrapperComponent
      isExpanded={isExpanded}
      hideOnMobile={hideOnMobile}
      isNonClothing={isNonClothing}
    >
      {tile(false, 'relative', true)}
      {tile(isExpanded, 'absolute')}
    </WrapperComponent>
  );
};

const ExpansionContainer = styled('div', {
  shouldForwardProp: (prop) =>
    !['hideOnMobile', 'expanded'].includes(prop.toString()),
})<{
  expanded: boolean;
  position?: CSSProperties['position'];
  hideOnMobile: boolean;
}>(({ theme, expanded = false, position = 'relative', hideOnMobile }) => ({
  backgroundColor: 'var(--color-white)',

  [theme.breakpoints.down('sm')]: {
    display: hideOnMobile ? 'none' : undefined,
  },

  [theme.breakpoints.up('sm')]: {
    position,
  },

  top: 0,
  left: 0,
  right: 0,
  height: '100%',
  zIndex: expanded ? 1 : 0,
}));

const LinkContainer = styled(Box)<{
  position?: CSSProperties['position'];
}>(({ theme, position = 'relative' }) => ({
  color: 'unset',

  [theme.breakpoints.up('sm')]: {
    position,
  },

  inset: '0 0 0 0',
  display: 'block',
  height: '100%',
})) as typeof Box;

const ShadowContainer = styled('div')<{ expanded: boolean }>(
  ({ theme, expanded }) => ({
    display: expanded ? undefined : 'grid',
    gridTemplateRows: expanded ? 'auto auto auto' : 'auto 1fr',
    height: expanded ? undefined : '100%',

    [theme.breakpoints.up('sm')]: {
      boxShadow: expanded ? '0px 0px 24px 4px #0000002B' : '',
    },

    '& > div': {
      cursor: expanded ? 'auto' : 'pointer',
    },
  }),
);

const ImageContainer = styled('div')(({ theme }) => ({
  backgroundColor: 'var(--color-white)',
  aspectRatio: '4 / 3',
  display: 'flex',
  justifyContent: 'center',
  paddingTop: 'var(--spacer-m)',
  paddingBottom: 'var(--spacer-m)',
  overflow: 'hidden',

  '.gatsby-image-wrapper': {
    height: '100%',

    img: {
      objectFit: 'contain !important',
    },
  },
}));

const ImageVariant = styled('div')<{ active: boolean }>(
  ({ theme, active }) => ({
    height: active ? 'auto' : 0,
    opacity: active ? 1 : 0,
    alignSelf: 'stretch',
    width: active ? '100%' : 0,
  }),
);

const ContentContainer = styled('div', {
  shouldForwardProp: (prop) => prop !== 'expanded',
})<{ expanded: boolean }>(({ theme, expanded }) => ({
  backgroundColor: 'var(--color-white)',
  borderTop: 'var(--spacer-xxs) solid var(--color-light-grey)',
  padding: 'var(--spacer-xs) 20px var(--spacer-m)',
  justifySelf: expanded ? undefined : 'stretch',

  [theme.breakpoints.up('sm')]: {
    borderTop: expanded
      ? 'var(--spacer-xxs) solid var(--color-white)'
      : 'var(--spacer-xxs) solid var(--color-light-grey)',
  },

  '.active & ': {
    borderColor: 'var(--color-white)',
    padding: '10px 20px 20px',
  },
}));

const Title = styled('h4')(({ theme }) => ({
  ...theme.typography.h5,
  margin: 0,

  [theme.breakpoints.up('sm')]: {
    ...theme.typography.h4,
  },
}));

const CollectionName = styled('p')(({ theme }) => ({
  ...theme.typography.body,
  fontWeight: theme.typography.fontWeightMedium,
  margin: 0,

  [theme.breakpoints.up('sm')]: {
    ...theme.typography.footnote,
  },
}));

const VariantsInfo = styled('p')(({ theme }) => ({
  ...theme.typography.footnote,
  margin: 'var(--spacer-m) 0 0',
}));

const ColorName = styled('p')(({ theme }) => ({
  ...theme.typography.body,
  margin: 'var(--spacer-s) 0 var(--spacer-m) 0',

  [theme.breakpoints.up('sm')]: {
    ...theme.typography.footnote,
  },
}));

const DetailsContainer = styled('div')<{ expanded: boolean }>(
  ({ theme, expanded = false }) => ({
    backgroundColor: 'var(--color-white)',
    flexDirection: 'column',
    alignItems: 'flex-start',
    padding: '0 20px 20px',
    display: expanded ? 'flex' : 'none',
    position: 'relative',
    zIndex: 3,
  }),
);

const IconContainer = styled('div')(
  ({ expanded = false }: { expanded: boolean }) => ({
    display: expanded ? 'flex' : 'none',
    width: '100%',
    justifyContent: 'flex-end',
    marginBottom: 'var(--spacer-m)',
    position: 'absolute',
    top: '20px',
    right: '20px',
  }),
);

const Icon = styled('button')({
  color: 'var(--color-dark-coal)',
  cursor: 'pointer',
  backgroundColor: 'transparent',
  border: 'none',

  '&:hover': {
    color: 'var(--color-light-red)',
  },

  svg: {
    width: '36px',
    height: '36px',
  },
});

const ColorContainer = styled('div')(({ theme }) => ({
  display: 'grid',
  gap: '15px',
  gridTemplateColumns: 'repeat(5, 45px)',
  width: '100%',

  [theme.breakpoints.up('sm')]: {
    gridTemplateColumns: 'repeat(3, 45px)',
    gap: '12px',
  },

  [theme.breakpoints.up('md')]: {
    gridTemplateColumns: 'repeat(5, 45px)',
    gap: '20px',
  },
}));

const KeyFacts = styled(DataGridList)(({ theme }) => ({
  ...theme.typography.footnote,
  marginTop: 'var(--spacer-m)',

  li: {
    paddingLeft: '16px',
  },
}));
